/**
 * Wire protocol and shared constants. Imported by BOTH the server and the Vue
 * app, so it must stay free of node-only and browser-only imports.
 */
import type { GitGraph, GitPush } from "./gitGraph.js";
import type { DeployStatus } from "./deploy.js";
import type { DashboardWidget, Flow, NodeLog, NodeStatus } from "./automation.js";

export const SERVICE = "todos";
export const DEFAULT_PORT = 3800;
export const WS_PATH = "/ws";
export const SESSION_COOKIE = "todos_sid";

/** One hour in ms. Task start/end are always snapped to a multiple of this. */
export const HOUR_MS = 3600_000;

/**
 * What a new task is assumed to cost when nobody says otherwise.
 *
 * Not zero: an unestimated task draws no bar at all, so a fresh task would land
 * on the board invisible. Half an hour is small enough to be wrong cheaply and
 * big enough to see.
 */
export const DEFAULT_EST_HOURS = 0.5;

export const STATUSES = ["todo", "doing", "blocked", "review", "done"] as const;
export type Status = (typeof STATUSES)[number];

/**
 * The ends of the urgency slider. Whole steps, so a sort has real ties to break
 * by rank.
 *
 * There is no zero. "none" was the step every task carried until somebody said
 * otherwise, which made the quietest badge on the board also the most common one
 * — and said nothing a reader could use. A task somebody bothered to write down
 * is at least `low`.
 */
export const URGENCY_MIN = 1;
export const URGENCY_MAX = 5;
/** A new task's urgency when nobody says: `medium`. The floor is for a task somebody
 *  judged low, not for one nobody judged. */
export const URGENCY_DEFAULT = 2;

/** What each urgency step reads as, from URGENCY_MIN upward. */
export const URGENCY_LABELS = ["low", "medium", "high", "urgent", "critical"] as const;

/**
 * The label for a step, clamped into the scale. Two things below the floor still
 * reach here and both mean "nothing said": a rollup of 0 (nothing unfinished
 * under this task) and a row written before the floor moved.
 */
export function urgencyLabel(step: number): string {
  const n = typeof step === "number" && Number.isFinite(step) ? Math.round(step) : URGENCY_MIN;
  return URGENCY_LABELS[Math.min(URGENCY_MAX, Math.max(URGENCY_MIN, n)) - URGENCY_MIN]!;
}

/** Statuses that count as finished when rolling progress up to a parent. */
export const DONE_STATUSES: ReadonlySet<Status> = new Set<Status>(["done"]);

export interface User {
  id: string;
  username: string;
  displayName: string;
  color: string;
  isAdmin: boolean;
}

/**
 * A team: the people who share a board, a message thread and a calendar.
 * Someone can be in several; every shared task, message and calendar entry is
 * in exactly one. Personal tasks and the money ledger are in none.
 */
export interface Team {
  id: string;
  name: string;
  /** A team that writes code: its tasks show a repo and branch and can be
   *  handed to Claude. Without it the board is a plain tree. */
  coding: boolean;
  /** A team that keeps a shopping list: the Shopping view is theirs. */
  shopping: boolean;
  /** The git folders whose repos it sees (shared/repoFolders.ts); empty is the default one. */
  repoFolders: string[];
  memberIds: string[];
  /** Members who may change the team's Settings (a site admin who is a member may too). */
  adminIds: string[];
  /** The switcher views its admins switched off (shared/teamViews.ts). */
  hiddenViews: string[];
}

/** An emailed invite to a team that has not been taken yet (server/teamSettings.ts). */
export interface TeamInvite {
  id: string;
  email: string;
  /** Who sent it, by user id. */
  invitedBy: string;
  createdAt: number;
  expiresAt: number;
}

/** What an invite link's page is told before anyone takes it. */
export interface InviteInfo {
  team: string;
  teamId: string;
  /** The inviter's display name. */
  invitedBy: string;
  email: string;
  /** Whoever is signed in here is already in the team. */
  member: boolean;
}

export interface Task {
  id: string;
  parentId: string | null;
  /**
   * The team a shared task belongs to, set on every task in its tree. Null on
   * a personal task, and only then. A task never moves between teams.
   */
  teamId: string | null;
  /**
   * Set on a personal task: the one user who can see it. Null for everything
   * on the shared board. Inherited by every sub-task, and a task never moves
   * between a personal tree and a shared one (server/tasks.ts).
   */
  privateTo: string | null;
  rank: string;
  title: string;
  notes: string;
  status: Status;
  /** Unix ms, snapped to the hour. Null means "not scheduled yet". */
  startMs: number | null;
  endMs: number | null;
  estHours: number | null;
  /**
   * Manual completion, 0..1. Null means "read it off the status" — a task
   * nobody has touched the slider on is 0% until it is done, exactly as before.
   * Only meaningful on a leaf: a parent's progress is rolled up from its
   * children and never stored. See shared/rollup.ts.
   */
  progress: number | null;
  /**
   * How pressing this is, URGENCY_MIN..URGENCY_MAX; a task nobody has ranked is
   * `low`, not nothing. Set by hand on any task. What a parent SORTS by is
   * rolled up from its unfinished descendants as well (shared/rollup.ts), so one
   * urgent sub-task lifts the whole tree it sits in.
   */
  urgency: number;
  /**
   * Human time logged against this task, one entry per person who has logged
   * any: two people pairing on a task are both working, and one shared total
   * would either lose one of them or count the pair as one. Claude's time is
   * not here — the tmux view reads it off its sessions.
   */
  time: TaskTime[];
  repo: string | null;
  branch: string | null;
  /** Kept off the Claude queue. Every undone task with a repo is queued for
   *  Claude (server/claudeQueue.ts) unless this is set. */
  claudeHidden: boolean;
  /** Needs a person with root: an admin's job, never Claude's, so never on the
   *  Claude queue. A Claude asks for one over MCP (create_admin_task). */
  adminTask: boolean;
  /** Once its worktree is merged it goes straight to done, not review
   *  (`reviewTaskAction`). Off unless ticked. */
  skipReview: boolean;
  assignees: string[];
  blockedBy: string[];
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  /** Bumped on every write; clients send it back as `ifVersion` to detect conflicts. */
  version: number;
}

export const EVENT_KINDS = ["comment", "created", "status", "branch", "assign", "schedule", "title", "timer", "claude"] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

export interface TaskTime {
  userId: string;
  /** Hours this person has logged, entered by hand. */
  hours: number;
}

/** Hours logged across everyone. */
export function loggedHours(time: TaskTime[]): number {
  return time.reduce((sum, t) => sum + (Number.isFinite(t.hours) ? t.hours : 0), 0);
}

export interface TaskEvent {
  id: string;
  taskId: string;
  kind: EventKind;
  userId: string | null;
  /** Comment text; null for system events. */
  body: string | null;
  /** Structured payload for system events, e.g. {from:"todo", to:"doing"}. */
  data: Record<string, unknown> | null;
  createdAt: number;
}

/**
 * The drawer fields that take a lock while someone is in them. Text-shaped
 * fields only: a status chip or an assignee toggle is one click, over before
 * anyone else could collide with it, but a paragraph of notes is minutes of
 * typing that a second editor would silently throw away.
 */
export const EDITABLE_FIELDS = ["title", "notes", "repo", "branch", "estHours", "progress", "urgency"] as const;
export type EditableField = (typeof EDITABLE_FIELDS)[number];

/** One person's hold on one field of one task. */
export interface FieldLock {
  taskId: string;
  field: EditableField;
}

/** A post on the team message board — one thread for the whole board, not tied to a task. */
export interface Message {
  id: string;
  /** Each team has its own thread. */
  teamId: string;
  userId: string;
  body: string;
  createdAt: number;
}

/**
 * One notification, as a browser's service worker receives it over Web Push
 * and as the Android app collects it from its queue (server/notify.ts).
 */
export interface Notice {
  kind: "assigned" | "comment" | "message" | "claudeRun";
  title: string;
  body: string;
  /** The task it is about, which a tap opens; null for a team message. */
  taskId: string | null;
  /** The team a message was posted in, so a tap opens that team's thread. */
  teamId?: string | null;
  /** Notices sharing a tag replace each other on the device rather than piling up. */
  tag: string;
  createdAt: number;
}

/** Longest message the board accepts. Enough for a paragraph and a pasted stack trace. */
export const MESSAGE_MAX = 4000;

// ── shopping ────────────────────────────────────────────────────────────────
/**
 * A thing a team buys again and again. Deliberately not a task: it is never
 * finished, only bought and then needed once more, and the question asked of
 * it is "when did we last get this", which a task's status cannot answer.
 */
export interface ShoppingItem {
  id: string;
  teamId: string;
  name: string;
  /** On the list: somebody said it is needed and nobody has bought it since. */
  needed: boolean;
  /** The newest purchase, or null if it has never been bought. Derived from the log. */
  lastBoughtAt: number | null;
  /** How many purchases the log holds for it. */
  timesBought: number;
  /**
   * What it costs now, in hundredths (pence, cents), or null if nobody has
   * said. The price the NEXT purchase is logged at; each purchase keeps its
   * own copy, so changing this never rewrites what was already spent.
   */
  priceMinor: number | null;
  /** Where it is bought, as typed. "" when nobody has said. */
  store: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

/** One purchase of an item: an entry in the shopping log. */
export interface Purchase {
  id: string;
  itemId: string;
  teamId: string;
  /** Who bought it. */
  userId: string;
  boughtAt: number;
  /**
   * What it cost this time: the item's price when it was bought, correctable
   * afterwards. Null when the item had no price. Totals add these up, never
   * the item's current price.
   */
  priceMinor: number | null;
  /** Where it was bought this time. */
  store: string;
}

/** Longest item name. */
export const SHOPPING_NAME_MAX = 100;
/** Longest store name. */
export const SHOPPING_STORE_MAX = 60;

// ── availability ────────────────────────────────────────────────────────────
/**
 * What one person has said about one span of time.
 *
 * Three kinds, because coordinating needs both a negative and a positive
 * signal. `away` is not working at all; `busy` is working but spoken for, which
 * is a different answer to "can we pair on this at four?"; `free` is the one
 * that has to be said out loud — hours outside someone's usual pattern that
 * they are happy to be asked about.
 *
 * Deliberately NOT derived from tasks. A scheduled bar says what someone
 * intends to do, which is a guess; availability says when they exist, which is
 * a fact, and the two disagreeing is exactly what the calendar is for.
 */
export const AVAILABILITY_KINDS = ["away", "busy", "free"] as const;
export type AvailabilityKind = (typeof AVAILABILITY_KINDS)[number];

/** Longest note on an entry. A reason, not a conversation — that is what the message board is for. */
export const AVAILABILITY_NOTE_MAX = 200;

/**
 * Longest span one entry may cover. A sabbatical is three entries; a typo in a
 * year is one entry that would otherwise paint every calendar anyone opens.
 */
export const AVAILABILITY_MAX_HOURS = 24 * 90;

export interface Availability {
  id: string;
  /** Whose time this is. Not necessarily who wrote it — see `createdBy`. */
  userId: string;
  /** The team's calendar it is on. Someone in two teams keeps each separately. */
  teamId: string;
  kind: AvailabilityKind;
  /** Unix ms, snapped to the hour, like a task's schedule. */
  startMs: number;
  /** Exclusive, snapped, always after `startMs`. */
  endMs: number;
  note: string;
  /**
   * Whether the work this span covers should be handed to Claude.
   *
   * A flag on the absence rather than a task of its own: at the moment someone
   * marks themselves out is the moment they know whether anything needs
   * covering, and a separate list would be the thing nobody remembers to write.
   * The `note` says what. Never set on a `free` span — being around and wanting
   * cover is not a thing you can be.
   */
  needsClaude: boolean;
  /** Who entered it: yourself, or an admin marking a company holiday for everyone. */
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

/** What someone fills in. `userId` is only honoured for an admin; anyone else gets their own. */
export interface AvailabilityInput {
  kind: AvailabilityKind;
  startMs: number;
  endMs: number;
  note?: string;
  needsClaude?: boolean;
}

// ── finances ────────────────────────────────────────────────────────────────
/**
 * One line in a ledger: money that went out or came in.
 *
 * A ledger is either a person's own — private to them, no admin reads it — or
 * a team's, which every member of the team shares (server/finance.ts). Either
 * way it never enters the change log. Amounts are integer minor units (pence,
 * cents) in the ledger's currency, because a float that adds 0.1 and 0.2 is
 * not somewhere to keep money.
 */
export const LEDGER_KINDS = ["expense", "income"] as const;
export type LedgerKind = (typeof LEDGER_KINDS)[number];

/** Longest note on a ledger line, and longest category name. */
export const LEDGER_NOTE_MAX = 200;
export const LEDGER_CATEGORY_MAX = 40;

export interface LedgerEntry {
  id: string;
  /** Who wrote it — on a team's ledger, which member; on a person's, always them. */
  createdBy: string;
  kind: LedgerKind;
  /** Always positive; `kind` says which way it went. */
  amountMinor: number;
  /**
   * The calendar day it happened, `YYYY-MM-DD`. A day rather than a timestamp:
   * a receipt says "21 Sep", and turning that into an instant would move it to
   * the day before for anyone west of wherever the server thinks midnight is.
   */
  date: string;
  category: string;
  note: string;
  /** The regular payment that posted this line, or null for one entered by hand. */
  recurringId: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface LedgerInput {
  kind: LedgerKind;
  amountMinor: number;
  date: string;
  category?: string;
  note?: string;
}

/** How often a regular payment falls due: monthly, quarterly, yearly. */
export const RECURRING_EVERY = [1, 3, 12] as const;
export type RecurringEvery = (typeof RECURRING_EVERY)[number];

/**
 * A payment that repeats — rent, a car loan, insurance, a salary.
 *
 * It is not itself a line in the ledger. Each time one falls due the server
 * posts an ordinary `LedgerEntry` for it (with `recurringId` set), so the
 * totals, the CSV and editing one month's line all work exactly as for a line
 * typed in by hand. `postedThrough` is how it knows which it has done: a line
 * deleted by its owner (a skipped month) is not posted again.
 */
export interface Recurring {
  id: string;
  kind: LedgerKind;
  amountMinor: number;
  category: string;
  note: string;
  /** The first payment, `YYYY-MM-DD`. Its day of the month is the day every later one falls on. */
  start: string;
  everyMonths: RecurringEvery;
  /** The last day one may fall on, or null for open-ended. */
  until: string | null;
  /** The latest day a line has been posted for, or null before the first. */
  postedThrough: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface RecurringInput {
  kind: LedgerKind;
  amountMinor: number;
  start: string;
  everyMonths: RecurringEvery;
  until?: string | null;
  category?: string;
  note?: string;
}

/** A ledger's settings, per person or per team. `currency` is null until one is picked. */
export interface FinanceSettings {
  currency: string | null;
}

// ── Claude runs ─────────────────────────────────────────────────────────────
/**
 * `default` passes no --model and leaves the choice to the `claude` account on
 * the box. The rest are Claude Code's aliases rather than dated model ids, so a
 * queued run keeps meaning "the current Opus" after a new one ships.
 */
export const CLAUDE_MODELS = ["default", "fable", "opus", "sonnet", "haiku"] as const;
export type ClaudeModel = (typeof CLAUDE_MODELS)[number];

/**
 * Claude Code's --permission-mode values that make sense with nobody at the
 * keyboard. `default` is left out on purpose: it stops to ask, and there is no
 * one on the VPS to answer.
 */
export const CLAUDE_PERMISSION_MODES = ["plan", "acceptEdits", "auto", "bypassPermissions"] as const;
export type ClaudePermissionMode = (typeof CLAUDE_PERMISSION_MODES)[number];

/** What to do once the work is finished. `push` implies `commit`: there is nothing to push otherwise. */
export const CLAUDE_FINISH_STEPS = ["commit", "push", "review", "comment"] as const;
export type ClaudeFinishStep = (typeof CLAUDE_FINISH_STEPS)[number];

/** queued → running → one of the last three. A queued run can go straight to cancelled. */
export const CLAUDE_RUN_STATUSES = ["queued", "running", "succeeded", "failed", "cancelled"] as const;
export type ClaudeRunStatus = (typeof CLAUDE_RUN_STATUSES)[number];

/** Statuses that still own their worktree and branch, so a second run cannot claim either. */
export const CLAUDE_ACTIVE_STATUSES: ReadonlySet<ClaudeRunStatus> = new Set<ClaudeRunStatus>(["queued", "running"]);

/** Longest prompt accepted: room for a large task's whole brief, notes and all. It never rides a
 *  command line (tmux stops at 16 KB), so the bridge hands it over as a file or a paste. */
export const CLAUDE_PROMPT_MAX = 200_000;

/** What someone fills in to hand a task to Claude. The repo is not here: it is read off the task. */
export interface ClaudeRunInput {
  /** What a new branch is cut from. Ignored by the runner when `branch` already exists. */
  baseBranch: string;
  /** The branch Claude commits to. */
  branch: string;
  /** Directory name of the worktree; a name, never a path. */
  worktree: string;
  prompt: string;
  model: ClaudeModel;
  permissionMode: ClaudePermissionMode;
  onFinish: ClaudeFinishStep[];
}

/** A request for the `claude` user on the VPS to work a task in its own worktree. */
export interface ClaudeRun extends ClaudeRunInput {
  id: string;
  taskId: string;
  /** Snapshot of the task's repo (its own or inherited) when the run was asked for. */
  repo: string;
  requestedBy: string;
  status: ClaudeRunStatus;
  createdAt: number;
  updatedAt: number;
  /** When the runner picked it up, and when it stopped for any reason. */
  startedAt: number | null;
  finishedAt: number | null;
  /** Claude Code's session id, so a finished run can be resumed by hand with `claude -r`. */
  sessionId: string | null;
  /** Claude's closing message: the summary on success, the plan in plan mode. */
  result: string | null;
  /** Why it failed or was stopped. */
  error: string | null;
  costUsd: number | null;
  /** Commits on the branch made by this run, and whether they reached the server. */
  commits: number;
  pushed: boolean;
}

/** What the runner reports when a run ends. */
export interface ClaudeRunOutcome {
  status: "succeeded" | "failed";
  sessionId?: string | null;
  result?: string | null;
  error?: string | null;
  costUsd?: number | null;
  commits?: number;
  pushed?: boolean;
}

/** What the runner gets when it claims a run: the run, and the task it is for. */
export interface ClaimedRun {
  run: ClaudeRun;
  task: { id: string; title: string; url: string | null };
}

/**
 * Why a branch name would be refused by git, or null when it is fine. The
 * subset of `git check-ref-format` people actually trip over, checked here so
 * the modal can say so before a round trip.
 */
export function branchNameProblem(name: string): string | null {
  if (!name) return "required";
  if (name.length > 200) return "too long";
  if (/[\s~^:?*[\\\x00-\x1f\x7f]/.test(name)) return "no spaces or ~ ^ : ? * [ \\";
  if (name.startsWith("-")) return "cannot start with -";
  if (name.startsWith("/") || name.endsWith("/") || name.includes("//")) return "stray /";
  if (name.includes("..")) return "cannot contain ..";
  if (name === "@" || name.includes("@{")) return "cannot contain @{";
  if (name.endsWith(".")) return "cannot end with .";
  if (/(^|\/)\./.test(name)) return "no part may start with .";
  if (/\.lock(\/|$)/.test(name)) return "no part may end with .lock";
  return null;
}

/** Why a worktree name is refused, or null. It becomes a directory, so it stays a plain name. */
export function worktreeNameProblem(name: string): string | null {
  if (!name) return "required";
  if (name.length > 64) return "64 characters at most";
  if (!/^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(name)) return "letters, digits, . _ - only";
  return null;
}

/** Whatever was typed as a worktree name: each run of anything else becomes
 *  `-`, and the ends are trimmed to a letter or digit. "" means no worktree. */
export function toWorktreeName(text: string): string {
  return text
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .slice(0, 64)
    .replace(/^[._-]+|[._-]+$/g, "");
}

/** A title as a worktree-safe name: "Fix the login page!" → "fix-the-login-page". */
export function slugify(text: string, max = 40): string {
  return text
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, max)
    .replace(/^-+|-+$/g, "");
}

/**
 * The worktree a task's Claude gets when nobody named one: its title, slugged
 * as it stands — "Fix the login page when it times out" → "fix-the-login-page-when-it-times-out"
 * (cut to `slugify()`'s 40). Common words are kept: dropping them made names that read wrong.
 */
export function worktreeNameFor(title: string): string {
  return slugify(title);
}

/**
 * `base`, or — when `taken` says a worktree of that name is somebody else's —
 * `base-2`, `base-3`…, the first free one, kept within the 64 characters a
 * name may have.
 */
export function freeWorktreeName(base: string, taken: (name: string) => boolean): string {
  if (!taken(base)) return base;
  for (let n = 2; ; n++) {
    const suffix = `-${n}`;
    const name = base.slice(0, 64 - suffix.length).replace(/[._-]+$/, "") + suffix;
    if (!taken(name)) return name;
  }
}

// ── Claude plan usage ───────────────────────────────────────────────────────
/** One of the plan's rolling limits: how much of it is gone, and when it refills. */
export interface UsageWindow {
  /** 0..100. */
  percent: number;
  /** Unix ms the window resets, or null when nobody said. */
  resetsAt: number | null;
}

/**
 * How much of its plan the `claude` account on the VPS has used, as last
 * reported by the runner (runner/usage.ts).
 *
 * Normalised on the runner, which is the only place that knows the shape of the
 * endpoint it is read from — an undocumented one Claude Code itself uses. If
 * that shape changes, a window becomes null or the report becomes an error; it
 * never turns into a confidently wrong number on somebody's home screen.
 */
export interface ClaudeUsage {
  fiveHour: UsageWindow | null;
  weekly: UsageWindow | null;
  /** When the numbers above were read. Null until a read has ever succeeded. */
  readAt: number | null;
  /**
   * Why the latest attempt failed, when it did. The numbers are then the last
   * good ones, and `readAt` says how old — a stale reading is still a reading.
   */
  error: string | null;
  errorAt: number | null;
  /**
   * Plan usage spent somewhere other than this box — claude.ai, the desktop app,
   * Claude Code on another machine signed in to the same account — in the
   * current five-hour window (`outsideRise()` in shared/usage.ts). Absent from a
   * server that predates it; null when none has been seen in this window.
   */
  outside?: OutsideUsage | null;
  /** What the box spent in the interval ending at `readAt`, as the runner said; null for unknown. */
  localTokens?: number | null;
}

/** Five-hour-window points that rose while nothing on the box used Claude. */
export interface OutsideUsage {
  /** Points of the five-hour window put down to use elsewhere, summed over the window. */
  points: number;
  /** When the first and the latest such rise were read. */
  firstAt: number;
  lastAt: number;
  /** The five-hour window it was seen in: a new window starts it over. */
  resetsAt: number | null;
}

/** The machine's disk, load and memory (server/systemStats.ts), for the strip's lower meters. */
export interface SystemStats {
  /** The root filesystem, as df counts it; null when it could not be read. */
  disk: { usedBytes: number; totalBytes: number } | null;
  /** `availableBytes` is MemAvailable: page cache counts as free. */
  memory: { availableBytes: number; totalBytes: number };
  /** All cores' busy share, 0..100, since the previous reading (about a second); null on the first. */
  cpu: { percent: number } | null;
  /** The one-minute load average, against how many cores share it. */
  load: { load1: number; cores: number };
  readAt: number;
}

/** The stats view's charts (server/systemStats.ts `statsHistory`): percentages, 0..100, per bucket. */
/** The CPU cap on everything Claude runs (server/cpuCap.ts). */
export interface CpuCap {
  /** The most cores asked for, null for no cap; never set, what the units agree on. */
  cores: number | null;
  /** False while the cap was never set from todos: the box's is whatever root left. */
  set: boolean;
  /** The box's cores, the most a cap can be. */
  cpus: number;
  /** What each unit has in force, in cores (null: no cap); a unit that cannot be read is left out. */
  applied: { unit: string; cores: number | null }[];
}

export interface StatsHistory {
  from: number;
  to: number;
  /** How long each point averages over. */
  bucketMs: number;
  /** `disk` is null for a bucket where the disk could not be read; `tmux` (the
   *  bridge's sessions, averaged) and `tmuxMax` where no sample counted them. */
  system: { at: number; disk: number | null; cpu: number; cpuMax: number; ram: number; tmux: number | null; tmuxMax: number | null }[];
  /**
   * `outside`: five-hour points in the bucket put down to use off the box (0 when none).
   * `used`: five-hour points spent in the bucket (`fiveHourRise`, summed), a reset's
   * fall left out; null where no reading in it had one before to compare with.
   * `tokens`: what the box's Claudes spent in the bucket (runner/localUsage.ts), null
   * where no reading said; `repos`: that by repo ("" for none), which adds up to less
   * than `tokens` where a reading came from a runner that did not split it.
   */
  usage: {
    at: number;
    fiveHour: number | null;
    weekly: number | null;
    outside: number;
    used: number | null;
    tokens: number | null;
    repos: Record<string, number>;
  }[];
  /** Every repo with tokens in what is kept (STATS_KEEP_DAYS), most first: the same
   *  whatever the range asked for, so a repo keeps its colour across ranges. */
  repos: string[];
}

/** One of the largest things in a directory, for the Disk chart's breakdown (runner/disk.ts). */
export interface DiskEntry {
  name: string;
  bytes: number;
  dir: boolean;
}

/** `du -x -a --max-depth=1` of `path`, as the tmux bridge (the claude user) can read it. */
export interface DiskUsage {
  path: string;
  /** The whole of `path`. */
  bytes: number;
  /** The largest, biggest first; `otherBytes` is the rest put together. */
  entries: DiskEntry[];
  otherBytes: number;
  /** Something could not be read, so the sizes are at least these. */
  partial: boolean;
  readAt: number;
}

/** A running process, for the CPU and RAM charts' breakdowns (server/processes.ts). */
export interface ProcessRow {
  pid: number;
  user: string;
  /** The kernel's short name (`comm`). */
  name: string;
  command: string;
  /** Percent of ONE core over the moment it was measured, so a busy process on four can reach 400. */
  cpu: number;
  rssBytes: number;
}

/** One process in full, for a row of the CPU/RAM breakdown opened (server/processes.ts `processInfo`). */
export interface ProcessInfo {
  pid: number;
  name: string;
  user: string;
  /** The whole command line, argv joined by spaces. */
  command: string;
  /** The kernel's one-letter state, spelt out: "sleeping", "running"… */
  state: string;
  parent: { pid: number; name: string } | null;
  children: { pid: number; name: string }[];
  threads: number;
  nice: number;
  startedAt: number;
  /** Percent of one core over the second it was measured, as ProcessRow. */
  cpu: number;
  /** User + system CPU time over its whole life. */
  cpuSeconds: number;
  rssBytes: number;
  peakRssBytes: number | null;
  swapBytes: number | null;
  virtualBytes: number;
  /** Only readable for a process of todos's own user: null otherwise. */
  cwd: string | null;
  openFiles: number | null;
  readBytes: number | null;
  writeBytes: number | null;
}

/** The ranges the stats view offers, in hours; the history route takes only these. */
export const STATS_RANGES = [6, 24, 24 * 7, 24 * 30] as const;

/** What the runner sends: numbers, or why it has none. */
export type ClaudeUsageReport =
  | {
      fiveHour: UsageWindow | null;
      weekly: UsageWindow | null;
      /**
       * Tokens every Claude on the box spent since the previous reading
       * (runner/localUsage.ts), or null when it could not tell. An older runner
       * sends none, and then nothing is ever put down to use elsewhere.
       */
      localTokens?: number | null;
      /** The same count by repo, "" for a Claude working in none. Absent from an older runner. */
      localByRepo?: Record<string, number>;
    }
  | { error: string };

/**
 * The request the runner makes to open the five-hour window ahead of a "need
 * Claude" entry (runner/prime.ts): its prompt, which an admin may edit, and
 * what came of the last one it sent.
 */
export interface ClaudePrimer {
  prompt: string;
  last: PrimeResult | null;
}

/** One prime as the runner reported it. Tokens are null when Claude Code did not say. */
export interface PrimeResult {
  at: number;
  /** The prompt it was sent with, which may since have been edited. */
  prompt: string;
  response: string | null;
  error: string | null;
  /** Everything read, cache reads and writes included: all of it counts against the window. */
  inputTokens: number | null;
  outputTokens: number | null;
}

export interface Presence {
  userId: string;
  /** The task this person currently has open, if any. */
  taskId: string | null;
  /** The field they are typing in, if any. Everyone else sees it disabled. */
  editing: FieldLock | null;
}

/**
 * Server -> client. Everything except `presence`, `hello`, `reset`,
 * `messagesRead` and the ledger events is also
 * appended to the `changes` table under its `seq`, so a reconnecting client can
 * replay what it missed instead of refetching the world.
 */
export type ServerEvent =
  /** `build` is the web build the server serves (null in dev); a tab on another reloads.
   *  `deploying`: a deploy of todos is running (null: not known yet, just restarted). */
  | { t: "hello"; seq: number; me: User; users: User[]; teams: Team[]; build: string | null; deploying: boolean | null }
  | { t: "snapshot"; seq: number; tasks: Task[] }
  | { t: "taskUpsert"; seq: number; task: Task; opId?: string }
  | { t: "taskDelete"; seq: number; id: string; opId?: string }
  | { t: "event"; seq: number; taskId: string; event: TaskEvent }
  | { t: "userUpsert"; seq: number; user: User }
  | { t: "message"; seq: number; message: Message }
  | { t: "availabilityUpsert"; seq: number; entry: Availability }
  | { t: "availabilityDelete"; seq: number; id: string; teamId: string }
  /** A shopping item was added or changed — including its "last bought" after a purchase. */
  | { t: "shoppingItemUpsert"; seq: number; item: ShoppingItem }
  | { t: "shoppingItemDelete"; seq: number; id: string; teamId: string }
  /** A purchase was logged, or taken out of the log. */
  | { t: "purchaseUpsert"; seq: number; purchase: Purchase }
  | { t: "purchaseDelete"; seq: number; id: string; itemId: string; teamId: string }
  /** A Claude run was queued, picked up, finished or cancelled. */
  | { t: "claudeRun"; seq: number; run: ClaudeRun }
  /** Sent only to the reader's own sockets: another tab or device clears its badge too. */
  | { t: "messagesRead"; teamId: string; at: number }
  /**
   * A ledger changed: someone's own (`teamId` null), sent only to THEIR
   * sockets, or a team's, sent only to its members'. Never written to the
   * change log, which every client replays — so a reconnecting tab refetches
   * its ledger rather than replaying it. `teamId` says which ledger, so a tab
   * showing another one ignores it.
   */
  | { t: "ledgerUpsert"; teamId: string | null; entry: LedgerEntry }
  | { t: "ledgerDelete"; teamId: string | null; id: string }
  | { t: "recurringUpsert"; teamId: string | null; recurring: Recurring }
  | { t: "recurringDelete"; teamId: string | null; id: string }
  | { t: "financeSettings"; teamId: string | null; settings: FinanceSettings }
  /**
   * The git server took a push (its post-receive hook, server/gitHook.ts). Sent
   * to admins only — it is for the tmux view — and never logged: a tab that
   * missed one rereads the graph on reconnect anyway.
   */
  | { t: "gitPush"; push: GitPush }
  /** A repo's git graph, as the server just read it, decorated for the reader
   *  (`withWorktreeTasks`) — the git panel applies it, it does not refetch
   *  (server/graphPush.ts). Admins only, never logged; sent only when it changed. */
  | { t: "gitGraph"; repo: string; graph: GitGraph }
  /** A command a button started (`ran`) ended — its session got `ranAt`
   *  (`ranStatus` its exit, `failure` what a failure last printed) or was
   *  closed first (`gone`, `ranStatus` null) — server/runWatch.ts. Admins only,
   *  never logged: an off-screen run waits on it rather than polling the list. */
  | {
      t: "runEnded";
      repo: string;
      session: string;
      commandId: string;
      worktree: string | null;
      gone: boolean;
      ranStatus: number | null;
      failure: string | null;
    }
  /** How a repo's deploys stand (server/autoDeploy.ts): admins only, never
   *  logged. A tab that was away asks `GET /api/tmux/deploys` instead. */
  | { t: "deployStatus"; status: DeployStatus }
  /** The Claude queue moved (server/claudeQueue.ts `queueMoved`, coalesced to one
   *  per tick): admins only, never logged, sent per admin. `ids` are the queued
   *  tasks the reader may see that will start on their own (start mode not
   *  Manual) — the board's ring, applied as is; the tmux view rereads its list. */
  | { t: "claudeQueued"; ids: string[] }
  /** The Automation view (server/automation.ts): admins only, never logged. A
   *  tab that was away refetches `GET /api/automation/flows` and the dashboard. */
  | { t: "automationFlow"; flow: Flow }
  | { t: "automationFlowDelete"; id: string }
  /** Nodes' badges, batched. */
  | { t: "automationStatus"; statuses: NodeStatus[] }
  | { t: "automationLog"; entries: NodeLog[] }
  /** `widget` null: taken off the dashboard. */
  | { t: "automationDashboard"; flowId: string; nodeId: string; widget: DashboardWidget | null }
  /** A deploy of todos itself started or ended (server/selfDeploy.ts): everyone,
   *  never logged — `hello` carries it to a tab that was away. */
  | { t: "deploying"; deploying: boolean }
  /** The reader's teams changed — a team's Settings renamed it, switched a view
   *  off, or took them in or out (server/teamSettings.ts). Never logged: `hello` carries it. */
  | { t: "teams"; teams: Team[] }
  | { t: "presence"; users: Presence[] }
  | { t: "reset" }
  /** Several of the above, in order: the server queues each socket's events and
   *  sends one frame per tick (server/ws.ts). Never nested; the socket unwraps it,
   *  so a store's handler never sees one. */
  | { t: "batch"; events: ServerEvent[] };

/**
 * Client -> server. Deliberately tiny: all mutations go over REST. `editing`
 * claims or releases a field lock; the server ignores a claim on a field
 * someone else already holds, and the presence broadcast says who won.
 */
export type ClientMsg =
  | { t: "resume"; since: number }
  | { t: "viewing"; taskId: string | null }
  | { t: "editing"; lock: FieldLock | null };

/** Snap a unix-ms timestamp down to the hour it falls in. */
export function snapToHour(ms: number): number {
  return Math.floor(ms / HOUR_MS) * HOUR_MS;
}

/**
 * Build a browse URL for a task's branch. Supports "owner/repo" (assumed
 * GitHub) and any full https origin, e.g. "https://gitlab.com/owner/repo".
 * Returns null when there is nothing sensible to link to.
 */
export function branchUrl(repo: string | null, branch: string | null): string | null {
  if (!repo || !branch) return null;
  const enc = branch.split("/").map(encodeURIComponent).join("/");
  if (/^https?:\/\//.test(repo)) {
    const base = repo.replace(/\/+$/, "");
    return base.includes("gitlab") ? `${base}/-/tree/${enc}` : `${base}/tree/${enc}`;
  }
  if (/^[\w.-]+\/[\w.-]+$/.test(repo)) return `https://github.com/${repo}/tree/${enc}`;
  return null;
}

/**
 * The closest ancestor's repo or branch — what a task without its own is
 * working in. Derived on every read, never copied down onto the children, so
 * changing a parent's repo moves every sub-task that has not overridden it.
 *
 * Each field inherits on its own: a sub-task can set just a branch and still
 * pick the repo up from above. Guards against a parent cycle, which the server
 * refuses but a half-applied pair of moves could briefly show the client.
 */
export function inheritedField(
  task: Pick<Task, "id" | "parentId">,
  key: "repo" | "branch",
  get: (id: string) => Task | undefined,
): string | null {
  const seen = new Set<string>([task.id]);
  let parentId = task.parentId;
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = get(parentId);
    if (!parent) return null;
    if (parent[key]) return parent[key];
    parentId = parent.parentId;
  }
  return null;
}

/**
 * Whether any task above this one is `blocked`: a queued Claude under a
 * blocked parent is held as if its own task were (`startsOnItsOwn`). Guards
 * against a parent cycle, as `inheritedField` does.
 */
export function blockedAncestor(task: Pick<Task, "id" | "parentId">, get: (id: string) => Task | undefined): boolean {
  const seen = new Set<string>([task.id]);
  let parentId = task.parentId;
  while (parentId && !seen.has(parentId)) {
    seen.add(parentId);
    const parent = get(parentId);
    if (!parent) return false;
    if (parent.status === "blocked") return true;
    parentId = parent.parentId;
  }
  return false;
}
